(() => {
  // luxsol-stock-control/storefront-src/cart-payment-state.ts
  function readCartPaymentState(cart, variantDataFor, isDeposit) {
    const depositCount = cart.items.filter(isDeposit).length;
    const mixed = depositCount > 0 && depositCount < cart.items.length;
    const mode = depositCount === cart.items.length && cart.items.length ? "deposit" : mixed ? "mixed" : "full";
    const allEligible = cart.items.length > 0 && cart.items.every(
      (item) => Boolean(variantDataFor(item.variant_id)?.depositPlanId)
    );
    const quantityByVariant = /* @__PURE__ */ new Map();
    cart.items.forEach((item) => {
      const variantId = String(item.variant_id);
      quantityByVariant.set(
        variantId,
        (quantityByVariant.get(variantId) || 0) + Number(item.quantity || 0)
      );
    });
    const promises = [...quantityByVariant].map(([variantId, quantity]) => {
      const data = variantDataFor(variantId);
      if (!data?.managed) return { state: "UNMANAGED" };
      const schedule = Array.isArray(data.schedule) ? data.schedule : [];
      if (!schedule.length) return { state: String(data.state || "IN_STOCK") };
      let cumulative = 0;
      for (const slot of schedule) {
        cumulative += Number(slot.capacity || 0);
        if (quantity <= cumulative) {
          return {
            state: slot.date ? "PREORDER" : "IN_STOCK",
            date: slot.date || void 0
          };
        }
      }
      return { state: "SOLD_OUT" };
    });
    const promiseDates = promises.filter((promise) => promise.state === "PREORDER" && promise.date).map((promise) => promise.date).sort();
    const containsPreorder = promiseDates.length > 0;
    const overCapacity = promises.some((promise) => promise.state === "SOLD_OUT");
    const depositAllowed = containsPreorder && allEligible && !overCapacity;
    const invalidDeposit = depositCount > 0 && !depositAllowed;
    return {
      depositCount,
      mixed,
      mode,
      allEligible,
      containsPreorder,
      overCapacity,
      depositAllowed,
      invalidDeposit,
      promiseDate: promiseDates.at(-1)
    };
  }

  // cart-ajax-payment-20260929/runtime-entry.js
  var startCartPayment = () => {
    (() => {
      const source = document.querySelector("[data-luxsol-cart-deposit-source]");
      if (!source || window.luxsolDraftCartPaymentLoaded) return;
      window.luxsolDraftCartPaymentLoaded = true;
      const labelSource = source.querySelector("[data-luxsol-cart-labels]");
      const labels = labelSource?.dataset;
      if (!labels) return;
      const planName = labelSource?.dataset.planName || "Pay 50% deposit";
      const paymentData = (line) => ({
        depositPlanId: line.depositPlanId ? String(line.depositPlanId) : "",
        managed: !!line.managed,
        state: line.state,
        schedule: Array.isArray(line.schedule) ? line.schedule : []
      });
      const initialLinePlans = (() => {
        try {
          const data = JSON.parse(source.querySelector("[data-luxsol-cart-payment-lines]")?.textContent || "[]");
          return new Map(data.map((line) => [String(line.variantId), paymentData(line)]));
        } catch {
          return /* @__PURE__ */ new Map();
        }
      })();
      const variantDataFor = (variantId) => initialLinePlans.get(String(variantId)) || window.luxsolVariantPaymentData?.[String(variantId)] || (window.luxsolDepositPlanByVariant?.[String(variantId)] ? {
        depositPlanId: String(window.luxsolDepositPlanByVariant[String(variantId)]),
        managed: true
      } : void 0);
      const depositPlanFor = (variantId) => variantDataFor(variantId)?.depositPlanId;
      let refreshTimer;
      let changing = false;
      let syncingMetadata = false;
      let lastSignature = "";
      let renderGeneration = 0;
      let paymentDataRequest;
      let readFailureCount = 0;
      let presentationFailed = false;
      const rootUrl = () => window.Shopify?.routes?.root || "/";
      const money = (minor, currency) => new Intl.NumberFormat(
        document.documentElement.lang || "en-GB",
        { style: "currency", currency, minimumFractionDigits: 2 }
      ).format(minor / 100);
      const date = (isoDate) => new Intl.DateTimeFormat(
        "en-GB",
        { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }
      ).format(/* @__PURE__ */ new Date(`${isoDate}T12:00:00Z`));
      const isDeposit = (item) => item.selling_plan_allocation?.selling_plan?.name === planName;
      const checkoutCharge = (item) => {
        if (!isDeposit(item)) return item.final_line_price;
        const charge = item.selling_plan_allocation?.selling_plan?.checkout_charge;
        if (!charge) return Math.round(item.final_line_price / 2);
        if (charge.value_type === "price") return Number(charge.value) * item.quantity;
        return Math.round(item.final_line_price * Number(charge.value) / 100);
      };
      const targets = () => [...document.querySelectorAll(
        ".drawer__footer, .cart__footer, .cart-order__recap, .cart-drawer__summary, .cart-summary__inner"
      )];
      const checkoutControls = () => [...document.querySelectorAll(
        '[name="checkout"], [href*="/checkout"], [data-testid="Checkout-button"]'
      )];
      const setCheckoutBlocked = (blocked) => {
        blocked = blocked || presentationFailed;
        window.luxsolDraftCartPaymentReady = !blocked;
        checkoutControls().forEach((control) => {
          if (blocked) {
            control.dataset.luxsolCheckoutBlocked = "true";
            control.setAttribute("aria-disabled", "true");
            if ("disabled" in control) control.disabled = true;
          } else if (control.dataset.luxsolCheckoutBlocked === "true") {
            delete control.dataset.luxsolCheckoutBlocked;
            control.removeAttribute("aria-disabled");
            if ("disabled" in control) control.disabled = false;
          }
        });
      };
      const paymentNotice = (message, error = false) => {
        const node = document.querySelector("#cart-drawer .lcd-payment-feedback");
        if (!node) return;
        node.textContent = message;
        node.hidden = false;
        node.toggleAttribute("data-error", error);
      };
      const setPaymentBusy = (busy) => {
        const drawer = document.querySelector("#cart-drawer");
        drawer?.toggleAttribute("data-payment-busy", busy);
        drawer?.querySelector('[slot="footer"]')?.setAttribute("aria-busy", String(busy));
        document.querySelectorAll("[data-luxsol-cart-payment-mode]").forEach((input) => {
          if (busy) {
            if (!input.disabled) input.dataset.paymentBusyDisabled = "";
            input.disabled = true;
          } else if ("paymentBusyDisabled" in input.dataset) {
            delete input.dataset.paymentBusyDisabled;
            input.disabled = false;
          }
        });
        drawer?.querySelectorAll("line-item-quantity input").forEach((input) => {
          if (busy) {
            if (!input.disabled) input.dataset.paymentBusyDisabled = "";
            input.disabled = true;
          } else if ("paymentBusyDisabled" in input.dataset) {
            delete input.dataset.paymentBusyDisabled;
            input.disabled = false;
          }
        });
      };
      const refreshPresentation = async () => {
        const drawer = document.querySelector("#cart-drawer");
        const drawerSection = drawer?.closest(".shopify-section");
        const isCartPage = window.location.pathname.replace(/\/$/, "").endsWith("/cart");
        const mainCart = isCartPage ? document.querySelector("main .cart__footer, main .cart-order__recap, main .cart-summary__inner")?.closest(".shopify-section") : null;
        const sections = [...new Set([drawerSection, mainCart].filter(Boolean).map((node) => node.id.replace(/^shopify-section-/, "")))];
        if (!sections.length) {
          presentationFailed = true;
          throw new Error(labels.updateError);
        }
        try {
          const documents = /* @__PURE__ */ new Map();
          for (const id of sections) {
            const path = mainCart && id === mainCart.id.replace(/^shopify-section-/, "") ? window.location.pathname : rootUrl();
            const url = new URL(path, window.location.origin);
            url.searchParams.set("section_id", id);
            const preview = new URLSearchParams(window.location.search).get("preview_theme_id");
            if (preview) url.searchParams.set("preview_theme_id", preview);
            const response = await fetch(url.pathname + url.search, { cache: "no-store", credentials: "same-origin", headers: { Accept: "text/html" } });
            if (!response.ok) throw new Error(labels.updateError);
            documents.set(id, new DOMParser().parseFromString(await response.text(), "text/html"));
          }
          const freshDrawer = drawerSection ? documents.get(drawerSection.id.replace(/^shopify-section-/, ""))?.querySelector("#cart-drawer") : null;
          const freshMain = mainCart ? documents.get(mainCart.id.replace(/^shopify-section-/, ""))?.getElementById(mainCart.id) : null;
          if (drawer && !freshDrawer || mainCart && !freshMain) throw new Error(labels.updateError);
          const body = drawer?.shadowRoot?.querySelector('[part~="body"]');
          const scroll = body?.scrollTop;
          const inner = drawer?.querySelector(".cart-drawer__inner");
          const freshInner = freshDrawer?.querySelector(".cart-drawer__inner");
          const footer = drawer?.querySelector('[slot="footer"]');
          const freshFooter = freshDrawer?.querySelector('[slot="footer"]');
          if (inner && freshInner && footer && freshFooter) {
            inner.replaceChildren(...freshInner.childNodes);
            footer.replaceWith(freshFooter);
          } else if (drawer && freshDrawer) {
            drawer.replaceChildren(...freshDrawer.childNodes);
          }
          if (mainCart && freshMain) mainCart.replaceChildren(...freshMain.childNodes);
          if (body && Number.isFinite(scroll)) body.scrollTop = scroll;
          presentationFailed = false;
        } catch {
          presentationFailed = true;
          throw new Error("Your payment selection is saved, but the cart display could not refresh. Reload this page before checkout.");
        }
      };
      const createChoice = (name, label, detail, checked, disabled = false) => {
        const choice = document.createElement("label");
        choice.className = "luxsol-cart-deposit__choice";
        const input = document.createElement("input");
        input.type = "radio";
        input.name = "luxsol-cart-payment-mode";
        input.value = name;
        input.checked = checked;
        input.disabled = changing || disabled;
        input.dataset.luxsolCartPaymentMode = "";
        const copy = document.createElement("span");
        const title = document.createElement("strong");
        const description = document.createElement("small");
        title.textContent = label;
        description.textContent = detail;
        copy.append(title, description);
        choice.append(input, copy);
        return choice;
      };
      const createRow = (label, value, emphasis = false) => {
        const row = document.createElement("div");
        row.className = emphasis ? "luxsol-cart-deposit__row luxsol-cart-deposit__row--due" : "luxsol-cart-deposit__row";
        const term = document.createElement("span");
        const amount = document.createElement("strong");
        term.textContent = label;
        amount.textContent = value;
        row.append(term, amount);
        return row;
      };
      const createPanel = (cart, state) => {
        const panel = document.createElement("section");
        panel.className = "luxsol-cart-deposit";
        panel.dataset.luxsolCartDeposit = "";
        panel.setAttribute("aria-label", labels.heading);
        const heading = document.createElement("p");
        heading.className = "luxsol-cart-deposit__heading";
        heading.textContent = labels.heading;
        const choices = document.createElement("div");
        choices.className = "luxsol-cart-deposit__choices";
        choices.append(
          createChoice("full", labels.payFull, labels.payFullDetail, state.mode === "full"),
          createChoice("deposit", labels.payDeposit, labels.payDepositDetail, state.mode === "deposit", !state.depositAllowed)
        );
        panel.append(heading, choices);
        if (state.promiseDate) {
          const promise = document.createElement("div");
          promise.className = "luxsol-cart-deposit__promise";
          promise.append(
            Object.assign(document.createElement("span"), { textContent: labels.expectedStock }),
            Object.assign(document.createElement("strong"), { textContent: date(state.promiseDate) })
          );
          panel.append(promise);
        }
        if (state.mode === "deposit") {
          const dueToday = cart.items.reduce((total, item) => total + checkoutCharge(item), 0);
          const dueLater = Math.max(0, cart.total_price - dueToday);
          const totals = document.createElement("div");
          totals.className = "luxsol-cart-deposit__totals";
          totals.append(
            createRow(labels.orderValue, money(cart.total_price, cart.currency)),
            createRow(labels.dueToday, money(dueToday, cart.currency), true),
            createRow(labels.dueLater, money(dueLater, cart.currency))
          );
          panel.append(totals);
          const note = document.createElement("p");
          note.className = "luxsol-cart-deposit__note";
          note.textContent = labels.note;
          panel.append(note);
        }
        const problem = state.overCapacity ? labels.overCapacity : state.mixed ? labels.mixedError : state.invalidDeposit && !state.containsPreorder ? labels.preorderOnly : state.containsPreorder && !state.allEligible ? labels.unavailable : "";
        if (problem) {
          const error = document.createElement("p");
          error.className = "luxsol-cart-deposit__error";
          error.setAttribute("role", "alert");
          error.textContent = problem;
          panel.append(error);
        }
        if (changing) {
          const status = document.createElement("p");
          status.className = "luxsol-cart-deposit__status";
          status.setAttribute("role", "status");
          status.textContent = labels.applying;
          panel.append(status);
        }
        return panel;
      };
      const readState = (cart) => readCartPaymentState(cart, variantDataFor, isDeposit);
      const fetchCart = async () => {
        const response = await fetch(`${rootUrl()}cart.js`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin"
        });
        if (!response.ok) throw new Error(labels.updateError);
        return response.json();
      };
      const ensurePaymentData = async (cart) => {
        if (cart.items.every((item) => variantDataFor(item.variant_id))) return;
        if (!paymentDataRequest) {
          paymentDataRequest = (async () => {
            const response = await fetch(`${rootUrl()}cart`, {
              headers: { Accept: "text/html" },
              credentials: "same-origin",
              cache: "no-store"
            });
            if (!response.ok) throw new Error(labels.updateError);
            const page = new DOMParser().parseFromString(await response.text(), "text/html");
            const content = page.querySelector("[data-luxsol-cart-payment-lines]")?.textContent;
            const lines = JSON.parse(content || "null");
            if (!Array.isArray(lines) || lines.some((line) => !line || !line.variantId)) throw new Error(labels.updateError);
            for (const line of lines) initialLinePlans.set(String(line.variantId), paymentData(line));
          })().finally(() => {
            paymentDataRequest = void 0;
          });
        }
        await paymentDataRequest;
        if (cart.items.some((item) => !variantDataFor(item.variant_id))) throw new Error(labels.updateError);
      };
      const showReadError = () => {
        setCheckoutBlocked(true);
        lastSignature = "";
        document.querySelectorAll("[data-luxsol-cart-deposit]:not([data-luxsol-cart-read-error])").forEach((panel) => panel.remove());
        for (const target of targets()) {
          if (target.querySelector("[data-luxsol-cart-read-error]")) continue;
          const panel = document.createElement("section");
          panel.className = "luxsol-cart-deposit";
          panel.dataset.luxsolCartDeposit = "";
          panel.dataset.luxsolCartReadError = "";
          panel.setAttribute("role", "alert");
          const link = document.createElement("a");
          link.href = `${rootUrl()}cart`;
          link.textContent = labels.updateError;
          panel.append(link);
          target.prepend(panel);
        }
      };
      const render = async () => {
        if (changing || syncingMetadata) return;
        const generation = ++renderGeneration;
        setCheckoutBlocked(true);
        try {
          let cart = await fetchCart();
          await ensurePaymentData(cart);
          if (generation !== renderGeneration || changing) return;
          if (!cart.items.length) {
            document.querySelectorAll("[data-luxsol-cart-deposit]").forEach((panel) => panel.remove());
            lastSignature = "";
            readFailureCount = 0;
            setCheckoutBlocked(false);
            return;
          }
          let state = readState(cart);
          if (!state.containsPreorder && state.depositCount > 0 && !state.overCapacity) {
            if (!window.luxsolApplyCartMode) throw new Error(labels.updateError);
            changing = true;
            try {
              await window.luxsolApplyCartMode({ cart, mode: "full", depositPlanFor, rootUrl, labels });
              const updated = await fetchCart();
              await ensurePaymentData(updated);
              if (readState(updated).mode !== "full") throw new Error(labels.updateError);
              await refreshPresentation();
              changing = false;
              lastSignature = "";
              scheduleRender();
              return;
            } catch (error) {
              changing = false;
              throw error;
            }
          }
          if (!window.luxsolSyncCheckoutMetadata) throw new Error(labels.updateError);
          syncingMetadata = true;
          let metadataChanged;
          try {
            metadataChanged = await window.luxsolSyncCheckoutMetadata({ cart, state, labels, rootUrl, date });
          } finally {
            syncingMetadata = false;
          }
          if (metadataChanged) {
            await refreshPresentation();
            cart = await fetchCart();
            await ensurePaymentData(cart);
            state = readState(cart);
          }
          readFailureCount = 0;
          if (!state.containsPreorder && !state.depositCount && !state.overCapacity) {
            document.querySelectorAll("[data-luxsol-cart-deposit]").forEach((panel) => panel.remove());
            lastSignature = "";
            setCheckoutBlocked(false);
            return;
          }
          setCheckoutBlocked(state.mixed || state.invalidDeposit || state.overCapacity);
          const renderTargets = targets();
          const signature = JSON.stringify({
            token: cart.token,
            total: cart.total_price,
            items: cart.items.map((item) => [item.key, item.quantity, isDeposit(item)]),
            mode: state.mode,
            eligible: state.allEligible,
            promiseDate: state.promiseDate,
            overCapacity: state.overCapacity
          });
          if (signature === lastSignature && document.querySelectorAll("[data-luxsol-cart-deposit]").length === renderTargets.length) return;
          lastSignature = signature;
          document.querySelectorAll("[data-luxsol-cart-deposit]").forEach((panel) => panel.remove());
          renderTargets.forEach((target) => {
            const panel = createPanel(cart, state);
            const checkout = target.querySelector('[name="checkout"], [href*="/checkout"]');
            const actions = checkout?.closest(".cart__ctas");
            if (actions && target.contains(actions)) actions.insertAdjacentElement("beforebegin", panel);
            else if (checkout) checkout.insertAdjacentElement("beforebegin", panel);
            else target.prepend(panel);
          });
        } catch {
          if (generation !== renderGeneration || changing) return;
          if (readFailureCount++ < 3) {
            window.setTimeout(render, 400);
            return;
          }
          showReadError();
        }
      };
      const applyMode = async (mode) => {
        if (changing || mode !== "full" && mode !== "deposit") return;
        changing = true;
        renderGeneration += 1;
        setCheckoutBlocked(true);
        setPaymentBusy(true);
        paymentNotice("Updating your payment amount\u2026");
        try {
          const cart = await fetchCart();
          await ensurePaymentData(cart);
          const state = readState(cart);
          if (mode === "deposit" && !state.depositAllowed) {
            throw new Error(state.containsPreorder ? labels.unavailable : labels.preorderOnly);
          }
          if (!window.luxsolApplyCartMode) throw new Error(labels.updateError);
          if (cart.items.some((item) => isDeposit(item) !== (mode === "deposit"))) await window.luxsolApplyCartMode({ cart, mode, depositPlanFor, rootUrl, labels });
          const updated = await fetchCart();
          await ensurePaymentData(updated);
          const confirmed = readState(updated);
          if (confirmed.mode !== mode || confirmed.mixed || confirmed.invalidDeposit || confirmed.overCapacity) throw new Error(labels.updateError);
          await window.luxsolSyncCheckoutMetadata({ cart: updated, state: confirmed, labels, rootUrl, date });
          await refreshPresentation();
          presentationFailed = false;
          changing = false;
          lastSignature = "";
          await render();
          setPaymentBusy(false);
          paymentNotice(mode === "deposit" ? "50% deposit selected. Your amount due today is shown below." : "Full payment selected. Your order value is shown below.");
          requestAnimationFrame(() => {
            const drawer = document.querySelector("#cart-drawer[open]");
            const radio = (drawer || document).querySelector(`[data-luxsol-cart-payment-mode][value="${mode}"]`);
            radio?.focus({ preventScroll: true });
          });
        } catch (error) {
          changing = false;
          setPaymentBusy(false);
          setCheckoutBlocked(true);
          await render();
          setCheckoutBlocked(true);
          const message = error instanceof Error ? error.message : labels.updateError;
          paymentNotice(message, true);
          document.querySelectorAll("[data-luxsol-cart-deposit]").forEach((panel) => {
            let errorNode = panel.querySelector(".luxsol-cart-deposit__error");
            if (!errorNode) {
              errorNode = document.createElement("p");
              errorNode.className = "luxsol-cart-deposit__error";
              errorNode.setAttribute("role", "alert");
              panel.append(errorNode);
            }
            errorNode.textContent = message;
            panel.querySelectorAll("input").forEach((input) => {
              input.disabled = false;
            });
          });
        }
      };
      const scheduleRender = () => {
        if (!isCartPage && !cartDrawer?.hasAttribute("open")) {
          setCheckoutBlocked(true);
          return;
        }
        window.clearTimeout(refreshTimer);
        refreshTimer = window.setTimeout(render, 150);
      };
      const cartDrawer = document.getElementById("cart-drawer");
      const isCartPage = window.location.pathname.replace(/\/$/, "").endsWith("/cart");
      document.addEventListener("change", (event) => {
        if (changing && event.target?.closest?.("line-item-quantity")) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
      document.addEventListener("click", (event) => {
        if (changing && event.target?.closest?.("line-item-quantity, [data-luxsol-remove-build]")) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
      document.addEventListener("change", (event) => {
        if (event.target?.matches?.("[data-luxsol-cart-payment-mode]")) applyMode(event.target.value);
        else scheduleRender();
      });
      document.addEventListener("click", (event) => {
        const checkout = event.target?.closest?.('[data-luxsol-checkout-blocked="true"]');
        if (!checkout) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        document.querySelector("[data-luxsol-cart-deposit]")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, true);
      document.addEventListener("submit", (event) => {
        const checkout = event.submitter || checkoutControls().find((control) => control.form === event.target);
        if (checkout?.dataset.luxsolCheckoutBlocked !== "true") return;
        event.preventDefault();
        event.stopImmediatePropagation();
        document.querySelector("[data-luxsol-cart-deposit]")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, true);
      document.addEventListener("cart:updated", scheduleRender);
      document.addEventListener("cart:refresh", scheduleRender);
      document.addEventListener("luxsol:deposit-plans-updated", scheduleRender);
      const cartObserver = new MutationObserver((records) => {
        if (records.some((record) => record.type === "attributes" && record.attributeName === "open")) {
          if (cartDrawer?.hasAttribute("open")) scheduleRender();
          return;
        }
        const cartChanged = records.some((record) => {
          const target = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
          if (target?.closest?.("[data-luxsol-cart-deposit]")) return false;
          return [...record.addedNodes, ...record.removedNodes].some((node) =>
            node.nodeType === Node.ELEMENT_NODE && !node.matches?.("[data-luxsol-cart-deposit]"));
        });
        if (cartChanged) scheduleRender();
      });
      if (cartDrawer) cartObserver.observe(cartDrawer, { attributes: true, attributeFilter: ["open"], childList: true, subtree: true });
      if (isCartPage) {
        const main = document.querySelector("main");
        if (main) cartObserver.observe(main, { childList: true, subtree: true });
      }
      if (isCartPage || cartDrawer?.hasAttribute("open")) render();
      else setCheckoutBlocked(true);
    })();
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", startCartPayment, { once: true });
  else startCartPayment();
})();
